import { gql } from "@apollo/client";
import { faSave } from "@fortawesome/free-solid-svg-icons";
import { uuid } from "config/app-constants";
import { graph } from "config/axios-context";
import { useRef } from "react";
import { IdNameType } from "types/common-types";
import { SortEnumType, TagsQuery, TagsQueryVariables } from "types/graphql";
import { saveTag } from "types/person-types";
import useAppContext from "utilities/hooks/useAppContext";
import MultiSelectInput, { MultiSelectInputPropsType } from "../../../../../common/form/inputs/MultiSelectInput";

const personTagsQuery = gql`
query Tags($where: PersonTagFilterInput, $order: [PersonTagSortInput!], $skip: Int, $take: Int) {
  personTags {
    items(where: $where, order: $order, skip: $skip, take: $take) {
      id
      name
    }
  }
}`;

export type TagFormType = IdNameType<string | number, string> & {
    tagId: number | string
}

type TagsInputPropsType = Partial<MultiSelectInputPropsType<TagFormType>>

const TagsInput = (props: TagsInputPropsType) => {
    const context = useAppContext();
    const realTags = props.value?.filter(x => x.tagId ? true : false) || [];
    const multiselectRef = useRef<MultiSelectInput<TagFormType>>(null)
    const defaultConfig: MultiSelectInputPropsType<TagFormType> = {
        label: 'fields.tags.name',
        name: 'tags',
        checkIfItemExists: (item, value) => item.name.toLowerCase() === value.name.toLowerCase(),
        dataSource: {
            request: async params => {
                try {
                    let variables: TagsQueryVariables = {
                        where: {
                            id: {
                                nin: props.value?.map(v => Number(v.tagId)) || [-1]
                            },
                            or: [
                                {
                                    name: {
                                        contains: params.searchText || ''
                                    }
                                },
                                {
                                    normalizedName: {
                                        contains: params.searchText?.toLowerCase() || ''
                                    }
                                }
                            ]
                        },
                        take: params.take,
                        skip: params.skip || 0,
                        order: {
                            name: SortEnumType.Asc
                        }
                    }

                    const response = await graph<TagsQuery, TagsQueryVariables>({
                        query: personTagsQuery,
                        variables
                    });

                    if (response.errors) {
                        throw response.errors;
                    }

                    return response.data!.personTags!.items.map(item => ({
                        tagId: item.id,
                        id: uuid(),
                        name: item.name
                    }));
                } catch (error: any) {
                    context.notify('error', 'error.loading-data', error.message);
                    throw error;
                }
            }
        },
        getItemId: item => item.id,
        view: {
            inputView: item => item?.name || ''
        },
        onChange: async (v, e) => {
            props.onChange && props.onChange(v, e);
        }
    }

    const config = { ...defaultConfig, ...props };

    config.suffix = [
        {
            icon: faSave,
            name: 'save',
            title: 'button.save',
            onClick: async e => {
                const inputValue = multiselectRef.current?.getInputValue() || '';
                if (!inputValue || props.value?.find(x => x.name === inputValue)) return;
                let tagId = realTags.find(x => x.name === inputValue)?.tagId || 0;
                if (!tagId) {
                    tagId = await saveTag({
                        id: 0,
                        name: inputValue
                    }, context.notify);
                }
                props.onChange && props.onChange([
                    ...(props.value || []),
                    {
                        tagId: tagId,
                        id: uuid(),
                        name: inputValue
                    }
                ], e)
            }
        }
    ]

    return (
        <MultiSelectInput
            {...config}
            ref={multiselectRef}
            value={props.value}
        />
    );
};

export default TagsInput;
